import type { VideoFrameV2 } from '@mimir/cutter/frameProvider/frameProvider';
import type { WebGlWrapper } from '@mimir/browser/gldraw/webGlWrapper';
import { BrowserShaderContext } from '@mimir/browser/gldraw/browserShaderContext';
import type { BrowserShaderPipeline } from '@mimir/browser/gldraw/browserShaderContext';
import type { ShaderPipelineDefinition } from '@mimir/cutter/compositor/shaderContext';

const MAX_PENDING_FRAMES = 2;

export class RawFrameRenderer {
  private pendingFrames : VideoFrameV2[] = [];
  private shaderContext : BrowserShaderContext;
  private pipelines = new Map<string, BrowserShaderPipeline>();

  constructor(private webGlWrapper: WebGlWrapper) {
    this.shaderContext =
      new BrowserShaderContext(
        webGlWrapper,
        {
          allowTexImageSourceUse: true,
          allowDirectTextureUse: true,
        }
      );
  }

  renderFrame(videoFrame: VideoFrameV2): void {
    if (this.pendingFrames.length > MAX_PENDING_FRAMES) {
      throw new Error('This should never happen');
    }
    if (this.pendingFrames.length === MAX_PENDING_FRAMES) {
      this.pendingFrames[MAX_PENDING_FRAMES - 1].buffer.close();
      this.pendingFrames[MAX_PENDING_FRAMES - 1] = videoFrame;
    } else {
      this.pendingFrames.push(videoFrame);
      if (this.pendingFrames.length === 1) {
        requestAnimationFrame(() => this.renderPendingFrame());
      }
    }
  }

  renderPendingFrame() {
    if (this.pendingFrames.length > 0) {
      const [videoFrame] = this.pendingFrames.splice(0, 1);
      this.renderFrameInternal(videoFrame);
      if (this.pendingFrames.length > 0) {
        requestAnimationFrame(() => this.renderPendingFrame());
      }
    }
  }

  renderFrameInternal(videoFrame: VideoFrameV2) {
    const outputResolution = this.webGlWrapper.getCanvasResolution();

    const shaderVideoFrame =
      this.shaderContext.getShaderVideoFrame(videoFrame);

    const pipelineDefinition : ShaderPipelineDefinition = {
      pipelineInputs: [
        {
          type: 'video_frame',
          shaderInterface: shaderVideoFrame.interface,
        }
      ],
      subpasses: [
        {
          outputResolution,
          inputs: [
            {
              type: 'pipeline_input',
              name: 'videoFrameInput',
              pipelineInputIndex: 0,
            },
          ],
          fragmentShader: `
            vec4 fragment(vec2 uv) {
              vec4 tmp = videoFrameInput(uv);

              // There seems to be no way to get browsers to correcly apply
              // linear -> sRGB conversion when drawing to the visible
              // framebuffer in a canvas. Because of this we need to manually
              // do the linear -> sRGB conversion here.
              return vec4(pow(tmp.rgb, vec3(1.0 / 2.2)), tmp.a);
            }
          `
        }
      ]
    };

    const pipeline = this.getOrCreatePipeline(pipelineDefinition);

    pipeline.processToCanvasSync([{ type: 'video_frame', shaderVideoFrame }]);

    videoFrame.buffer.close();

    // TODO: This seems to be required on firefox, but it blocks everything else so
    // probably not the best idea.
    //gl.finish();
  }

  private getOrCreatePipeline(definition: ShaderPipelineDefinition): BrowserShaderPipeline {
    const key = JSON.stringify(definition);
    const existingPipeline = this.pipelines.get(key);
    if (existingPipeline !== undefined) {
      return existingPipeline;
    }
    const newPipeline = this.shaderContext.createPipelineSync(definition);
    this.pipelines.set(key, newPipeline);
    return newPipeline;
  }
}

